import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { updatePreferences } from '../api/userPreferences';
import { getSignupPlatforms, getCachedSignupPlatforms } from '../api/platforms';
import { getSubscriptionData } from '../api/subscription';
import aerochatLogo from '../assets/aerochat_logo_full.svg';
import wordpressIcon from '../assets/channel-icons/wordpress.svg';
import '../styles/pages/platform-selection.css';

// Platform icons arrive as raw SVG markup from the platforms API. Keep it to
// the SVG profile so the markup can only draw, never run script.
const sanitizeIconSvg = (svg) => DOMPurify.sanitize(String(svg || ''), { USE_PROFILES: { svg: true, svgFilters: true } });

const PlatformSelectionPage = ({ currentUser, onLoginSuccess }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  // Prime from the module-level cache (warmed on the signup page) so we can
  // render cards on first paint when the platforms list is already in memory.
  const cached = getCachedSignupPlatforms();
  const [platforms, setPlatforms] = useState(cached || []);
  const [loading, setLoading] = useState(!cached);
  const [showModal, setShowModal] = useState(false);
  const [modalPlatform, setModalPlatform] = useState(null);
  const [saving, setSaving] = useState(false);
  // AppSumo (lifetime-deal) accounts get extra guidance on the Shopify option:
  // pick Shopify's *free* plan and reuse their AppSumo email so the backend
  // preserves the lifetime plan instead of overwriting it. Detected the same
  // way as PricingPage — the current plan id starts with 'appsumo_'.
  const [isAppSumo, setIsAppSumo] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await getSubscriptionData();
        setIsAppSumo(String(data?.currentPlan || '').startsWith('appsumo_'));
      } catch {
        // Best-effort — if it fails we simply omit the extra guidance
      }
    })();
  }, []);

  useEffect(() => {
    if (cached) return;
    (async () => {
      try {
        const data = await getSignupPlatforms();
        setPlatforms(data || []);
      } catch {
        // Fallback: empty list — page will show no cards
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const savePlatformAndRoute = async (platformKey, route) => {
    if (saving) return;
    setSaving(true);
    try {
      await updatePreferences({ platformType: platformKey });
    } catch {
      // Continue even if preferences save fails — it can be retried
    }
    // Update localStorage so AdminApp picks up platformType on next render
    const stored = localStorage.getItem('aerochat_user');
    if (stored) {
      const user = JSON.parse(stored);
      user.platformType = platformKey;
      localStorage.setItem('aerochat_user', JSON.stringify(user));
    }
    // Navigate first, then update React state — avoids race condition where
    // AdminApp re-renders on /platform-select and redirects to /dashboard
    // before navigate can take effect.
    navigate(route, { replace: true });
    if (onLoginSuccess && currentUser) {
      onLoginSuccess({ ...currentUser, platformType: platformKey });
    }
    setSaving(false);
  };

  const handleSelect = (platform) => {
    if (platform.hasModal) {
      setModalPlatform(platform);
      setShowModal(true);
    } else {
      savePlatformAndRoute(platform.key, platform.onboardingRoute || '/getstarted/standard');
    }
  };

  const handleModalCta = () => {
    if (!modalPlatform) return;
    if (modalPlatform.modalCtaUrl) {
      // External redirect (e.g. Shopify App Store): open the URL but do NOT
      // persist platformType — the install isn't complete yet. The user stays
      // on this page and can either finish the external install (the Shopify
      // OAuth callback will set platformType) or pick a different platform.
      window.open(modalPlatform.modalCtaUrl, '_blank', 'noopener,noreferrer');
    } else {
      savePlatformAndRoute(modalPlatform.key, modalPlatform.onboardingRoute || '/getstarted/standard');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setModalPlatform(null);
  };

  // Close modal on Escape key
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  if (loading) {
    return (
      <div className="ps-page">
        <div className="ps-container">
          <div className="ps-header">
            <div className="ps-logo">
              <img src={aerochatLogo} alt="AeroChat" className="ps-logo__img" />
            </div>
            <h1 className="ps-title">{t('platformSelect.title')}</h1>
            <p className="ps-subtitle">{t('platformSelect.subtitle')}</p>
          </div>
          {/* Skeleton cards while loading */}
          <div className="ps-cards ps-cards--count-2">
            <div className="ps-card ps-card--skeleton"><div className="ps-skeleton ps-skeleton--icon" /><div className="ps-skeleton ps-skeleton--title" /><div className="ps-skeleton ps-skeleton--desc" /><div className="ps-skeleton ps-skeleton--badge" /></div>
            <div className="ps-card ps-card--skeleton"><div className="ps-skeleton ps-skeleton--icon" /><div className="ps-skeleton ps-skeleton--title" /><div className="ps-skeleton ps-skeleton--desc" /><div className="ps-skeleton ps-skeleton--badge" /></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ps-page">
      <div className="ps-container">
        {/* Header */}
        <div className="ps-header">
          <div className="ps-logo">
            <img src={aerochatLogo} alt="AeroChat" className="ps-logo__img" />
          </div>
          <h1 className="ps-title">{t('platformSelect.title')}</h1>
          <p className="ps-subtitle">{t('platformSelect.subtitle')}</p>
        </div>

        {/* Platform Cards — dynamic from API */}
        <div className={`ps-cards${platforms.length === 2 ? ' ps-cards--count-2' : platforms.length === 1 ? ' ps-cards--count-1' : ''}`}>
          {platforms.map((platform) => (
            <div
              key={platform.key}
              className={`ps-card ps-card--${platform.key}`}
              onClick={() => handleSelect(platform)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleSelect(platform)}
            >
              {platform.key === 'wordpress' ? (
                <div className="ps-card__icon">
                  <img src={wordpressIcon} width="30" height="30" alt="" />
                </div>
              ) : platform.iconSvg && (
                <div
                  className="ps-card__icon"
                  dangerouslySetInnerHTML={{ __html: sanitizeIconSvg(platform.iconSvg) }}
                />
              )}
              <p className="ps-card__name">{platform.name}</p>
              <p className="ps-card__desc">{platform.description}</p>
              {platform.badgeText && (
                <span className={`ps-badge ps-badge--${platform.badgeType || 'autosync'}`}>
                  {platform.badgeType === 'autosync' && <span className="ps-badge__dot" />}
                  {platform.badgeText}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Dynamic Modal */}
      {showModal && modalPlatform && (
        <div className="ps-modal-overlay" onClick={closeModal}>
          <div className="ps-modal" onClick={(e) => e.stopPropagation()}>
            <button className="ps-modal__close" onClick={closeModal} title="Go back">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            {modalPlatform.iconSvg && (
              <div
                className="ps-modal__icon"
                dangerouslySetInnerHTML={{ __html: sanitizeIconSvg(modalPlatform.iconSvg) }}
              />
            )}

            <h2 className="ps-modal__title">{modalPlatform.modalTitle}</h2>
            <p className="ps-modal__body">{modalPlatform.modalBody}</p>

            {modalPlatform.modalPerks && modalPlatform.modalPerks.length > 0 && (
              <div className="ps-modal__highlight">
                <div className="ps-modal__highlight-title">{t('platformSelect.modalPerksTitle')}</div>
                <ul className="ps-modal__perks">
                  {modalPlatform.modalPerks.map((perk, i) => (
                    <li key={i}><span className="ps-modal__perks-check">✓</span> {perk}</li>
                  ))}
                </ul>
              </div>
            )}

            {isAppSumo && modalPlatform.key === 'shopify' && (
              <div className="ps-modal__appsumo-note">
                <div className="ps-modal__appsumo-note-title">{t('platformSelect.appsumoShopifyNoteTitle')}</div>
                <p className="ps-modal__appsumo-note-body">{t('platformSelect.appsumoShopifyNote')}</p>
              </div>
            )}

            <div className="ps-modal__actions">
              <button className="ps-btn-primary" onClick={handleModalCta} disabled={saving}>
                {modalPlatform.modalCtaText || 'Continue'} ↗
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformSelectionPage;
